import { reportJobContentPreview, reportJobName } from "../utils";

// A thumbnail is 36px in a table row and 28px where the space is tighter.
const THUMBNAIL_SIZES = {
  regular: "h-9 w-9",
  compact: "h-7 w-7",
};

/**
 * What the report is of, under its name.
 *
 * Brochures resolve to artworks, so they get a strip of primary-image
 * thumbnails — the fastest way to recognise a document you queued ten minutes
 * ago. Contact / Invoice / Exhibition reports have no artwork behind them, so
 * they get their names instead. Either way the strip is capped and the
 * remainder collapses into a count, keeping every row the same height.
 */
const ReportJobContents = ({ items, hidden, size }) => {
  if (items.length === 0) return null;

  const names = items
    .map(item => item?.title)
    .filter(Boolean)
    .join(", ");
  const thumbnails = items.filter(item => item?.thumbnailUrl);

  return (
    <div className="mt-1.5">
      {thumbnails.length > 0 ? (
        <div className="flex flex-wrap items-center gap-1">
          {items.map(item => (
            <span
              key={item.id}
              className={`${THUMBNAIL_SIZES[size]} shrink-0 overflow-hidden rounded border border-gray-200 bg-gray-50`}
              title={[item?.title, item?.subtitle].filter(Boolean).join(" — ")}
            >
              {item?.thumbnailUrl ? (
                <img
                  className="h-full w-full object-cover"
                  src={item.thumbnailUrl}
                  alt={item?.title || ""}
                  loading="lazy"
                />
              ) : null}
            </span>
          ))}
          {hidden > 0 ? (
            <span className="ml-1 text-xs font-normal text-gray-500">
              +{hidden} more
            </span>
          ) : null}
        </div>
      ) : null}
      {names ? (
        <div className="max-w-sm truncate text-xs font-normal text-gray-500">
          {names}
          {hidden > 0 && thumbnails.length === 0 ? `, +${hidden} more` : ""}
        </div>
      ) : null}
    </div>
  );
};

/**
 * Which report this is, as the Reporting list's Report column tells it: the name
 * derived from the saved payload, then the works (or the names) behind it.
 *
 * Shared with the header's notification dropdown so a job reads the same
 * wherever it is listed — the name of the thing you asked for rather than the
 * flavour of report it is, which the section beside it already says.
 *
 * Type is inherited, so the size of the words follows whatever lists it. `size`
 * only sets the thumbnails, and `thumbnailLimit` cuts the strip short where
 * there isn't room for all of it.
 */
const ReportJobSummary = ({ job, size = "regular", thumbnailLimit = null }) => {
  const { items, hidden } = reportJobContentPreview(job, thumbnailLimit);

  return (
    <>
      <div className="max-w-sm truncate font-medium">{reportJobName(job)}</div>
      <ReportJobContents items={items} hidden={hidden} size={size} />
    </>
  );
};

export default ReportJobSummary;
